import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import type { Product } from '@shop-monorepo/types';
import { isValidOldPrice } from '@shop-monorepo/types';
import type { TableProps } from 'antd';
import { Button } from 'antd';
import React, { useMemo } from 'react';
import { generatePath } from 'react-router-dom';
import styled from 'styled-components';

import { BottomPagination } from '../../components/BottomPagination';
import { DeleteButton } from '../../components/DeleteButton';
import { EditButton } from '../../components/EditButton';
import {
  PageContainer,
  PageHeader,
  PageTitle,
  StyledTable,
  TableContainer,
} from '../../components/PageLayout';
import { ProductLink } from '../../components/ProductLink';
import { ProductMobileCard } from '../../components/ProductMobileCard';
import { RightAlignedSpace } from '../../components/RightAlignedSpace';
import { StatusIndicator } from '../../components/StatusIndicator';
import { useBrands } from '../../hooks/useBrandQueries';
import { useCategories } from '../../hooks/useCategoryQueries';
import { useIsMobile } from '../../hooks/useIsMobile';
import { useListNavigate } from '../../hooks/useListReturn';
import { usePagination } from '../../hooks/usePagination';
import {
  useDeleteProduct,
  useProducts,
  useUpdateProductActive,
  useUpdateProductPrice,
} from '../../hooks/useProductQueries';
import { useTableSorter } from '../../hooks/useTableSorter';
import { useBooleanParams, useStringParams } from '../../hooks/useUrlParam';
import { ROUTES } from '../../routes/routes';
import { formatCategoryName } from '../../utils/categoryUtils';
import { OLD_PRICE_ERROR } from '../../utils/currency';

import { ActiveCheckboxCell } from './ActiveCheckboxCell';
import { PriceInputCell } from './PriceInputCell';
import { ProductFilters } from './ProductFilters';
import { ProductSearch } from './ProductSearch';

const FiltersBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 8px;
`;

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Thumb = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: #edf2f7;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const ProductsPage: React.FC = () => {
  const goToDetail = useListNavigate();
  const { page, pageSize } = usePagination();
  const isMobile = useIsMobile();

  const { data: brands = [] } = useBrands();
  const { data: categories = [] } = useCategories();

  const booleanFilters = useBooleanParams(['isActive', 'isNew']);
  const stringFilters = useStringParams(['brand', 'category', 'q']);

  const filters = useMemo(
    () => ({ ...booleanFilters, ...stringFilters }),
    [booleanFilters, stringFilters],
  );

  const { sorter, handleTableChange } = useTableSorter();

  const { data: paginatedProducts, isLoading } = useProducts({
    page,
    limit: pageSize,
    filters,
    sorter,
  });
  const products = paginatedProducts?.hits || [];
  const totalProducts = paginatedProducts?.total || 0;

  const { mutate: deleteProduct } = useDeleteProduct();
  const { mutateAsync: updatePrice } = useUpdateProductPrice();
  const { mutateAsync: updateActive } = useUpdateProductActive();

  const columns: TableProps<Product>['columns'] = [
    {
      title: 'Назва',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name: string, record: Product) => (
        <NameCell>
          <Thumb>
            {(record.thumbnail || record.image) && (
              <img src={record.thumbnail || record.image} alt={name} />
            )}
          </Thumb>
          <ProductLink slug={record.slug}>{name}</ProductLink>
        </NameCell>
      ),
    },
    {
      title: 'Стара ціна',
      dataIndex: 'oldPrice',
      key: 'oldPrice',
      width: 100,
      render: (oldPrice: number | null, record: Product) => (
        <PriceInputCell
          value={oldPrice}
          allowEmpty
          validate={(coins) => (isValidOldPrice(record.price, coins) ? undefined : OLD_PRICE_ERROR)}
          onSave={(coins) => updatePrice({ id: record.id, price: { oldPrice: coins } })}
        />
      ),
    },
    {
      title: 'Нова ціна',
      dataIndex: 'price',
      key: 'price',
      width: 110,
      sorter: true,
      render: (price: number, record: Product) => (
        <PriceInputCell
          value={price}
          validate={(coins) =>
            isValidOldPrice(coins as number, record.oldPrice) ? undefined : OLD_PRICE_ERROR
          }
          onSave={(coins) => updatePrice({ id: record.id, price: { price: coins as number } })}
        />
      ),
    },
    {
      title: 'Бренд',
      dataIndex: 'brandId',
      key: 'brandId',
      render: (brandId: number) => brands.find((brand) => brand.id === brandId)?.name || 'Немає',
    },
    {
      title: 'Категорії',
      dataIndex: 'categoryIds',
      key: 'categoryIds',
      render: (categoryIds: number[]) =>
        categoryIds
          .map((id) => formatCategoryName(categories, id))
          .filter(Boolean)
          .join(', ') || 'Немає',
    },
    {
      title: 'Активний',
      dataIndex: 'isActive',
      key: 'isActive',
      sorter: true,
      render: (isActive: boolean, record: Product) => (
        <ActiveCheckboxCell
          value={isActive}
          onSave={(next) => updateActive({ id: record.id, isActive: next })}
        />
      ),
    },
    {
      title: 'Новий',
      dataIndex: 'isNew',
      key: 'isNew',
      sorter: true,
      render: (isNew: boolean) => <StatusIndicator isActive={isNew} />,
    },
    {
      title: 'Перегляди',
      dataIndex: 'views',
      key: 'views',
      sorter: true,
    },
    {
      title: 'Замовлення',
      dataIndex: 'ordersCount',
      key: 'ordersCount',
      sorter: true,
    },
    {
      title: 'Дія',
      key: 'action',
      align: 'right',
      render: (_, record) => (
        <RightAlignedSpace size="middle">
          <Button
            icon={<EyeOutlined />}
            onClick={() => goToDetail(generatePath(ROUTES.PRODUCT_VIEW, { slug: record.slug }))}
          />
          <EditButton
            onClick={() => goToDetail(generatePath(ROUTES.PRODUCT_EDIT, { slug: record.slug }))}
          />
          <DeleteButton onConfirm={() => deleteProduct(record.id)} entityName={record.name} />
        </RightAlignedSpace>
      ),
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Продукти</PageTitle>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => goToDetail(ROUTES.PRODUCT_CREATE)}
        >
          Додати продукт
        </Button>
      </PageHeader>
      <FiltersBar>
        <ProductFilters />
        <ProductSearch />
      </FiltersBar>
      {isMobile ? (
        <>
          {isLoading
            ? null
            : products.map((product) => (
                <ProductMobileCard
                  key={product.id}
                  product={product}
                  brandName={brands.find((b) => b.id === product.brandId)?.name}
                  onView={(slug) => goToDetail(generatePath(ROUTES.PRODUCT_VIEW, { slug }))}
                  onEdit={(slug) => goToDetail(generatePath(ROUTES.PRODUCT_EDIT, { slug }))}
                  onDelete={(id) => deleteProduct(id)}
                />
              ))}
        </>
      ) : (
        <TableContainer>
          <StyledTable
            columns={columns}
            dataSource={products}
            rowKey="id"
            loading={isLoading}
            pagination={false}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
          />
        </TableContainer>
      )}
      <BottomPagination total={totalProducts} />
    </PageContainer>
  );
};
